/* ==========================================================================
   SkinKandy PDP redesign (2026)
   Custom elements: pdp-product, pdp-gallery, pdp-countdown, pdp-stack, pdp-review-insights
   Relies on the theme's product-form.js (Add to cart + cart drawer) and pubsub.js.
   ========================================================================== */
(() => {
  if (window.SKPDP) return;

  const MOBILE_QUERY = '(max-width: 989px)';

  /* ---------- Utilities ---------- */

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const stripTags = (str) => String(str || '').replace(/<[^>]*>/g, '');

  function formatMoney(cents, format) {
    const value = typeof cents === 'string' ? Number(cents.replace(/[^\d-]/g, '')) : Number(cents) || 0;
    const fmt = stripTags(format || '${{amount}}');
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;

    const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      const fixed = (number / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };

    const match = fmt.match(placeholder);
    let amount;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals':
        amount = withDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        amount = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = withDelimiters(value, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        amount = withDelimiters(value, 2, ' ', ',');
        break;
      default:
        amount = withDelimiters(value, 2);
    }
    return fmt.replace(placeholder, amount);
  }

  function joinList(items) {
    if (items.length <= 1) return items.join('');
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
  }

  function getCartElement() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function publishCartUpdate(source, data) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source, cartData: data });
    }
  }

  /** Adds several items in one request and opens the theme's cart drawer. */
  async function addItemsToCart(items, opener, source = 'pdp') {
    const cart = getCartElement();
    const isDrawer = cart && cart.tagName === 'CART-DRAWER';
    const body = { items };

    if (isDrawer && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      if (opener && typeof cart.setActiveElement === 'function') cart.setActiveElement(opener);
    }

    const addUrl = `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || "We couldn't add that to your cart.");
    }

    publishCartUpdate(source, data);

    if (isDrawer && data.sections) {
      cart.classList.remove('is-empty');
      cart.renderContents(data);
    } else {
      window.location = (window.routes && window.routes.cart_url) || '/cart';
    }
    return data;
  }

  /* ---------- Lightbox (single instance, appended to body) ---------- */

  const Lightbox = {
    el: null,
    images: [],
    index: 0,
    opener: null,

    build() {
      if (this.el) return;
      const el = document.createElement('div');
      el.className = 'pdp-lightbox pdp-scope';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', 'Image viewer');
      el.hidden = true;
      el.innerHTML = `
        <figure class="pdp-lightbox__stage"><img class="pdp-lightbox__img" alt=""></figure>
        <button type="button" class="pdp-lightbox__btn pdp-lightbox__close" data-lb-close aria-label="Close">${icon('close')}</button>
        <button type="button" class="pdp-lightbox__btn pdp-lightbox__prev" data-lb-prev aria-label="Previous image">${icon('chevron-left')}</button>
        <button type="button" class="pdp-lightbox__btn pdp-lightbox__next" data-lb-next aria-label="Next image">${icon('chevron-right')}</button>
        <p class="pdp-lightbox__counter" aria-live="polite"></p>`;
      document.body.appendChild(el);

      this.el = el;
      this.img = el.querySelector('.pdp-lightbox__img');
      this.counter = el.querySelector('.pdp-lightbox__counter');
      this.prevBtn = el.querySelector('[data-lb-prev]');
      this.nextBtn = el.querySelector('[data-lb-next]');

      el.addEventListener('click', (event) => {
        if (event.target.closest('[data-lb-close]') || event.target === el || event.target.classList.contains('pdp-lightbox__stage')) {
          this.close();
        } else if (event.target.closest('[data-lb-prev]')) {
          this.go(-1);
        } else if (event.target.closest('[data-lb-next]')) {
          this.go(1);
        }
      });

      let touchX = null;
      el.addEventListener('touchstart', (e) => (touchX = e.changedTouches[0].clientX), { passive: true });
      el.addEventListener('touchend', (e) => {
        if (touchX === null) return;
        const dx = e.changedTouches[0].clientX - touchX;
        if (Math.abs(dx) > 50) this.go(dx < 0 ? 1 : -1);
        touchX = null;
      });

      this.onKeydown = this.onKeydown.bind(this);
    },

    open(images, index = 0, opener = null) {
      if (!images || !images.length) return;
      this.build();
      this.images = images;
      this.opener = opener;
      this.show(index);
      this.el.hidden = false;
      this.prevOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => this.el.classList.add('is-open'));
      document.addEventListener('keydown', this.onKeydown);
      this.el.querySelector('[data-lb-close]').focus();
    },

    close() {
      if (!this.el || this.el.hidden) return;
      this.el.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKeydown);
      document.documentElement.style.overflow = this.prevOverflow || '';
      const finish = () => {
        this.el.hidden = true;
        if (this.opener && typeof this.opener.focus === 'function') this.opener.focus({ preventScroll: true });
      };
      if (reducedMotion()) finish();
      else setTimeout(finish, 220);
    },

    show(index) {
      const total = this.images.length;
      this.index = (index + total) % total;
      const image = this.images[this.index];
      this.img.src = image.src;
      this.img.alt = image.alt || '';
      this.counter.textContent = `${this.index + 1} / ${total}`;
      const single = total < 2;
      this.prevBtn.hidden = single;
      this.nextBtn.hidden = single;
    },

    go(step) {
      this.show(this.index + step);
    },

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'ArrowLeft') {
        this.go(-1);
      } else if (event.key === 'ArrowRight') {
        this.go(1);
      } else if (event.key === 'Tab') {
        const focusable = Array.from(this.el.querySelectorAll('button:not([hidden])'));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    },
  };

  function icon(name) {
    const paths = {
      close: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
      'chevron-left': '<path d="m15 18-6-6 6-6"/>',
      'chevron-right': '<path d="m9 18 6-6-6-6"/>',
    };
    return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${paths[name] || ''}</svg>`;
  }

  window.SKPDP = { formatMoney, addItemsToCart, Lightbox };

  /* ==========================================================================
     <pdp-product> — variant selection, availability, quantity, ATC feedback,
     sticky mobile bar
     ========================================================================== */

  class PdpProduct extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-pdp-json]');
      if (!json) return;

      try {
        this.product = JSON.parse(json.textContent);
      } catch (e) {
        console.error('PDP: invalid product JSON', e);
        return;
      }

      this.sectionId = this.dataset.sectionId;
      this.form = document.getElementById(this.dataset.formId);
      this.idInput = this.form ? this.form.querySelector('input[name="id"]') : null;
      this.atc = this.querySelector('[data-pdp-atc]');
      this.atcLabel = this.querySelector('[data-pdp-atc-label]');
      this.payment = this.querySelector('[data-pdp-payment]');
      this.optionGroups = Array.from(this.querySelectorAll('[data-pdp-option]'));
      this.sticky = this.querySelector('[data-pdp-sticky]');
      this.labels = {
        add: this.dataset.labelAdd || 'Add to cart',
        added: this.dataset.labelAdded || 'Added to cart',
        soldOut: this.dataset.labelSoldOut || 'Sold out',
        unavailable: this.dataset.labelUnavailable || 'Unavailable',
      };

      const initial =
        this.product.variants.find((v) => v.id === this.product.selectedVariantId) || this.product.variants[0];
      this.selected = initial ? initial.options.slice() : [];
      this.variant = initial || null;

      this.addEventListener('change', this.onChange.bind(this));
      this.initQuantity();
      this.initRatingLink();
      this.initAddedFeedback();
      this.initStickyBar();

      this.update({ fromUser: false });
    }

    disconnectedCallback() {
      this.cartUnsubscribe?.();
      this.errorUnsubscribe?.();
      this.stickyObserver?.disconnect();
      document.body.classList.remove('pdp-has-sticky-bar');
    }

    /* --- Options --- */

    onChange(event) {
      const input = event.target;
      if (!input.matches('[data-pdp-option-input]')) return;
      const index = Number(input.dataset.optionIndex);
      this.selected[index] = input.value;

      // Later options: if the current choice no longer exists / is sold out, move to the nearest available value.
      for (let j = index + 1; j < this.product.options.length; j++) {
        if (this.isAvailable(j, this.selected[j])) continue;
        const values = this.valuesFor(j);
        const currentPos = values.indexOf(this.selected[j]);
        let best = null;
        let bestDistance = Infinity;
        values.forEach((value, pos) => {
          if (!this.isAvailable(j, value)) return;
          const distance = Math.abs(pos - currentPos);
          if (distance < bestDistance) {
            best = value;
            bestDistance = distance;
          }
        });
        if (best !== null) this.selected[j] = best;
      }

      this.update({ fromUser: true });
    }

    valuesFor(index) {
      const group = this.optionGroups.find((g) => Number(g.dataset.optionIndex) === index);
      if (group) return Array.from(group.querySelectorAll('[data-pdp-option-input]')).map((i) => i.value);
      return [...new Set(this.product.variants.map((v) => v.options[index]))];
    }

    matchingVariants(index, value) {
      return this.product.variants.filter((variant) => {
        for (let k = 0; k < index; k++) {
          if (variant.options[k] !== this.selected[k]) return false;
        }
        return variant.options[index] === value;
      });
    }

    isAvailable(index, value) {
      return this.matchingVariants(index, value).some((v) => v.available);
    }

    resolveVariant() {
      return (
        this.product.variants.find((v) => v.options.every((opt, i) => opt === this.selected[i])) || null
      );
    }

    update({ fromUser }) {
      this.variant = this.resolveVariant();
      this.renderOptions();
      this.renderVariant(fromUser);
    }

    renderOptions() {
      this.optionGroups.forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        const unavailable = [];

        group.querySelectorAll('[data-pdp-option-input]').forEach((input) => {
          const available = this.isAvailable(index, input.value);
          const label = group.querySelector(`label[for="${input.id}"]`);
          input.checked = input.value === this.selected[index];
          input.disabled = !available && !input.checked;
          if (label) label.classList.toggle('is-unavailable', !available);
          if (!available) unavailable.push(input.value);
        });

        group.querySelectorAll('[data-pdp-selected-value]').forEach((el) => {
          el.textContent = this.selected[index] || '';
        });

        const help = group.querySelector('[data-pdp-option-help]');
        if (!help) return;
        if (index === 0 || !unavailable.length) {
          help.hidden = true;
          help.textContent = '';
          return;
        }
        const context = this.selected.slice(0, index).join(' / ');
        let message = `${joinList(unavailable)} ${unavailable.length === 1 ? 'is' : 'are'} sold out in ${context}`;
        if (unavailable.length === 1) {
          const match = this.matchingVariants(index, unavailable[0]).find((v) => v.restock);
          message += match ? ` — ${match.restock}` : '.';
        } else {
          message += '.';
        }
        help.textContent = message;
        help.hidden = false;
      });
    }

    renderVariant(fromUser) {
      const variant = this.variant;
      const available = !!(variant && variant.available);
      const showCurrency = this.product.showCurrency;
      const fmt = showCurrency ? this.product.moneyWithCurrencyFormat : this.product.moneyFormat;

      // Price
      if (variant) {
        const current = this.querySelector('[data-pdp-price-current]');
        const compare = this.querySelector('[data-pdp-price-compare]');
        if (current) current.textContent = formatMoney(variant.price, fmt);
        if (compare) {
          const onSale = variant.compare_at_price > variant.price;
          compare.hidden = !onSale;
          compare.textContent = onSale ? formatMoney(variant.compare_at_price, fmt) : '';
        }
        const stickyPrice = this.querySelector('[data-pdp-sticky-price]');
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price, this.product.moneyFormat);
        const stickyImg = this.querySelector('[data-pdp-sticky-img]');
        if (stickyImg && variant.image) stickyImg.src = variant.image;
      }

      // Hidden variant input + buttons
      if (this.idInput) {
        this.idInput.value = variant ? variant.id : '';
        this.idInput.disabled = !available;
        if (fromUser) this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const label = !variant ? this.labels.unavailable : available ? this.labels.add : this.labels.soldOut;
      if (this.atc) {
        this.atc.disabled = !available;
        if (this.atcLabel && !this.addedTimer) this.atcLabel.textContent = label;
      }
      const stickyBtn = this.querySelector('[data-pdp-sticky-atc]');
      if (stickyBtn) {
        stickyBtn.disabled = !available;
        stickyBtn.textContent = label;
      }
      if (this.payment) this.payment.classList.toggle('is-disabled', !available);

      // Specifications (live)
      this.querySelectorAll('[data-pdp-spec-option]').forEach((el) => {
        const value = this.selected[Number(el.dataset.pdpSpecOption)];
        if (value !== undefined) el.textContent = value;
      });
      const sku = this.querySelector('[data-pdp-sku]');
      if (sku) {
        sku.textContent = variant && variant.sku ? variant.sku : '';
        sku.closest('[data-pdp-sku-row]')?.classList.toggle('hidden', !(variant && variant.sku));
      }

      if (!fromUser) return;

      // URL
      if (variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      document.dispatchEvent(
        new CustomEvent('pdp:variant-change', {
          detail: { sectionId: this.sectionId, productId: this.product.id, variant, product: this.product },
        })
      );
    }

    /* --- Quantity (1–max) --- */

    initQuantity() {
      this.qtyInput = this.querySelector('[data-pdp-qty-input]');
      if (!this.qtyInput) return;
      const min = Number(this.qtyInput.min) || 1;
      const max = Number(this.qtyInput.max) || 10;
      const minus = this.querySelector('[data-pdp-qty-step="-1"]');
      const plus = this.querySelector('[data-pdp-qty-step="1"]');

      const set = (value) => {
        const next = Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
        this.qtyInput.value = next;
        if (minus) minus.disabled = next <= min;
        if (plus) plus.disabled = next >= max;
      };

      this.querySelectorAll('[data-pdp-qty-step]').forEach((button) => {
        button.addEventListener('click', () => {
          set(parseInt(this.qtyInput.value, 10) + Number(button.dataset.pdpQtyStep));
        });
      });
      this.qtyInput.addEventListener('change', () => set(parseInt(this.qtyInput.value, 10)));
      set(parseInt(this.qtyInput.value, 10));
    }

    /* --- Rating link → reviews --- */

    initRatingLink() {
      const link = this.querySelector('[data-pdp-scroll-reviews]');
      if (!link) return;
      link.addEventListener('click', (event) => {
        const target = document.getElementById('reviews');
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
        target.focus({ preventScroll: true });
      });
    }

    /* --- "Added to cart" label for 1.6s after a successful add --- */

    initAddedFeedback() {
      if (!this.form || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.form.addEventListener('submit', () => {
        this.awaitingAdd = true;
      });
      this.cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!this.awaitingAdd || !event || event.source !== 'product-form') return;
        this.awaitingAdd = false;
        if (!this.atcLabel) return;
        clearTimeout(this.addedTimer);
        this.atcLabel.textContent = this.labels.added;
        this.addedTimer = setTimeout(() => {
          this.addedTimer = null;
          this.renderVariant(false);
        }, 1600);
      });
      this.errorUnsubscribe = subscribe(PUB_SUB_EVENTS.cartError, () => {
        this.awaitingAdd = false;
      });
    }

    /* --- Sticky bar: visible only once the main ATC has scrolled out of view (mobile) --- */

    initStickyBar() {
      if (!this.sticky || !this.atc || !('IntersectionObserver' in window)) return;
      const mq = window.matchMedia(MOBILE_QUERY);
      const syncBodyPadding = () => document.body.classList.toggle('pdp-has-sticky-bar', mq.matches);
      syncBodyPadding();
      mq.addEventListener?.('change', syncBodyPadding);

      const setVisible = (visible) => {
        this.sticky.classList.toggle('is-visible', visible);
        this.sticky.setAttribute('aria-hidden', String(!visible));
        this.sticky.inert = !visible;
        if (!visible) this.sticky.setAttribute('inert', '');
        else this.sticky.removeAttribute('inert');
      };

      this.stickyObserver = new IntersectionObserver(
        ([entry]) => setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0),
        { threshold: 0 }
      );
      this.stickyObserver.observe(this.atc);

      this.sticky.querySelector('[data-pdp-sticky-atc]')?.addEventListener('click', () => {
        if (!this.form || this.atc.disabled) return;
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.atc);
        else this.atc.click();
      });
    }
  }

  /* ==========================================================================
     <pdp-gallery> — desktop grid + lightbox, mobile carousel + counter + arrows
     ========================================================================== */

  class PdpGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-pdp-gallery-track]');
      if (!this.track) return;
      this.items = Array.from(this.track.querySelectorAll('.pdp-gallery__item'));
      this.counter = this.querySelector('[data-pdp-gallery-current]');
      this.expand = this.querySelector('[data-pdp-gallery-expand]');
      this.prevButton = this.querySelector('[data-pdp-gallery-prev]');
      this.nextButton = this.querySelector('[data-pdp-gallery-next]');
      this.mq = window.matchMedia(MOBILE_QUERY);
      this.prevButton?.addEventListener('click', () => this.goTo(this.currentIndex() - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex() + 1));

      const data = this.querySelector('[data-pdp-lightbox-data]');
      try {
        this.images = data ? JSON.parse(data.textContent) : [];
      } catch (e) {
        this.images = [];
      }

      const hero = this.items[0];
      this.heroButton = hero ? hero.querySelector('[data-pdp-lightbox-index]') : null;
      this.heroImg = hero ? hero.querySelector('img') : null;
      this.heroOriginal = this.heroImg ? this.heroImg.cloneNode(true) : null;
      this.heroOriginalIndex = this.heroButton ? this.heroButton.dataset.pdpLightboxIndex : '0';

      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-pdp-lightbox-index]');
        if (!trigger || !this.images.length) return;
        Lightbox.open(this.images, Number(trigger.dataset.pdpLightboxIndex) || 0, trigger);
      });

      let ticking = false;
      this.track.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            this.updateCounter();
            ticking = false;
          });
        },
        { passive: true }
      );

      this.onVariantChange = this.onVariantChange.bind(this);
      document.addEventListener('pdp:variant-change', this.onVariantChange);
      this.onModeChange = () => {
        this.restoreHero();
        this.updateCounter();
      };
      this.mq.addEventListener?.('change', this.onModeChange);
      this.updateCounter();
    }

    disconnectedCallback() {
      document.removeEventListener('pdp:variant-change', this.onVariantChange);
      this.mq?.removeEventListener?.('change', this.onModeChange);
    }

    currentIndex() {
      const width = this.track.clientWidth || 1;
      return Math.min(this.items.length - 1, Math.max(0, Math.round(this.track.scrollLeft / width)));
    }

    // Mobile carousel: slide to an image (arrows).
    goTo(index) {
      const target = Math.min(this.items.length - 1, Math.max(0, index));
      this.track.scrollTo({
        left: this.track.clientWidth * target,
        behavior: reducedMotion() ? 'auto' : 'smooth',
      });
    }

    // Counter and arrow states follow the slide in view (mobile only; both are hidden on desktop).
    updateCounter() {
      if (!this.mq.matches) return;
      const index = this.currentIndex();
      if (this.counter) this.counter.textContent = String(index + 1);
      if (this.prevButton) this.prevButton.disabled = index <= 0;
      if (this.nextButton) this.nextButton.disabled = index >= this.items.length - 1;
    }

    onVariantChange(event) {
      if (event.detail.sectionId !== this.dataset.sectionId) return;
      const mediaId = event.detail.variant && event.detail.variant.media_id;
      if (!mediaId) return;
      const position = this.items.findIndex((item) => Number(item.dataset.mediaId) === Number(mediaId));
      if (position < 0) return;

      if (this.mq.matches) {
        // Mobile: slide the carousel to the finish's image (420ms ease).
        this.restoreHero();
        this.track.scrollTo({
          left: this.track.clientWidth * position,
          behavior: reducedMotion() ? 'auto' : 'smooth',
        });
      } else {
        // Desktop: show the finish's image in the packshot slot; the rest of the grid keeps its fixed order.
        if (position === 0) {
          this.restoreHero();
          return;
        }
        const sourceImg = this.items[position].querySelector('img');
        const sourceButton = this.items[position].querySelector('[data-pdp-lightbox-index]');
        if (!sourceImg || !this.heroImg) return;
        const clone = sourceImg.cloneNode(true);
        clone.sizes = this.heroOriginal.sizes;
        clone.loading = 'eager';
        this.heroImg.replaceWith(clone);
        this.heroImg = clone;
        if (sourceButton) {
          this.heroButton.dataset.pdpLightboxIndex = sourceButton.dataset.pdpLightboxIndex;
          if (this.expand) this.expand.dataset.pdpLightboxIndex = sourceButton.dataset.pdpLightboxIndex;
        }
      }
    }

    restoreHero() {
      if (!this.heroOriginal || !this.heroImg || this.heroImg.isEqualNode(this.heroOriginal)) return;
      const original = this.heroOriginal.cloneNode(true);
      this.heroImg.replaceWith(original);
      this.heroImg = original;
      if (this.heroButton) this.heroButton.dataset.pdpLightboxIndex = this.heroOriginalIndex;
      if (this.expand) this.expand.dataset.pdpLightboxIndex = this.heroOriginalIndex;
    }
  }

  /* ==========================================================================
     <pdp-countdown> — "Ends in 2d 14h" when the offer ends within N days;
     under 24h "Ends in {h}h {m}m"
     ========================================================================== */

  class PdpCountdown extends HTMLElement {
    connectedCallback() {
      this.endsAt = Number(this.dataset.endsAt) * 1000;
      this.windowMs = (Number(this.dataset.windowDays) || 7) * 86400000;
      this.tick();
      this.timer = setInterval(() => this.tick(), 30000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const diff = this.endsAt - Date.now();
      if (!this.endsAt || Number.isNaN(diff) || diff <= 0 || diff > this.windowMs) {
        this.hidden = true;
        if (diff <= 0) clearInterval(this.timer);
        return;
      }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      this.textContent = days >= 1 ? `Ends in ${days}d ${hours}h` : `Ends in ${hours}h ${minutes}m`;
      this.hidden = false;
    }
  }

  /* ==========================================================================
     <pdp-stack> — Style The Stack bundle: the product being viewed + 3 random pieces
     (found by Liquid, shuffled here), Buy 3 Get 1 Free total (for every 4 ticked items
     with the offer tag, the cheapest is free), and one request to add all ticked pieces.
     ========================================================================== */

  // Fisher–Yates shuffle (returns a new array)
  const shuffle = (items) => {
    const result = items.slice();
    for (let i = result.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  };

  class PdpStack extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-stack-list]');
      this.shufflePieces();
      this.rows = Array.from(this.querySelectorAll('[data-stack-row]'));
      this.button = this.querySelector('[data-stack-add]');
      this.buttonLabel = this.querySelector('[data-stack-add-label]');
      this.error = this.querySelector('[data-pdp-error]');
      this.moneyFormat = this.dataset.moneyFormat;
      this.moneyWithCurrency =
        this.dataset.showCurrency === 'true' ? this.dataset.moneyWithCurrencyFormat : this.dataset.moneyFormat;

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-stack-check]')) this.update();
      });
      this.button?.addEventListener('click', () => this.add());

      this.onVariantChange = this.onVariantChange.bind(this);
      document.addEventListener('pdp:variant-change', this.onVariantChange);
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('pdp:variant-change', this.onVariantChange);
    }

    // Liquid renders the first matching pieces as rows and the rest in a <template>. The page HTML is cached
    // and shared, so the random pick happens here: shuffle all of them, keep as many as are shown, renumber.
    shufflePieces() {
      const pool = this.querySelector('template[data-stack-pool]');
      const shown = Array.from(this.list.querySelectorAll('[data-stack-row][data-number]'));
      if (shown.length < 2 && !pool) return;
      const extra = pool ? Array.from(pool.content.querySelectorAll('[data-stack-row]')) : [];
      const picks = shuffle(shown.concat(extra)).slice(0, shown.length);
      pool?.remove();
      shown.forEach((row) => row.remove());
      picks.forEach((row, index) => {
        row.dataset.number = index + 1;
        const number = row.querySelector('.pdp-stack__num');
        if (number) number.textContent = index + 1;
        this.list.append(row);
      });
      if (picks[0] !== shown[0]) this.setPhoto(picks[0].dataset);
    }

    // The photo is the 2nd image of piece 1 (its 1st if it only has one), else the section's fallback photo.
    setPhoto(data) {
      const frame = this.querySelector('[data-stack-photo]');
      if (!frame) return;
      const own = Boolean(data.photoSrc);
      const src = own ? data.photoSrc : this.dataset.fallbackSrc;
      frame.querySelector('.pdp-stack__img')?.remove();
      if (!src) return;
      const img = document.createElement('img');
      img.className = 'pdp-stack__img';
      img.alt = (own ? data.photoAlt : this.dataset.fallbackAlt) || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      if (own && data.photoSrcset) {
        img.sizes = '(min-width: 990px) 440px, 100vw';
        img.srcset = data.photoSrcset;
      }
      img.src = src;
      const position = own ? data.photoPosition : this.dataset.fallbackPosition;
      if (position) img.style.objectPosition = position;
      frame.append(img);
    }

    onVariantChange(event) {
      const variant = event.detail.variant;
      const row = this.rows.find((r) => r.dataset.current === 'true');
      if (!row || !variant) return;
      row.dataset.variantId = variant.id;
      row.dataset.price = variant.price;
      row.dataset.available = String(variant.available);
      row.classList.toggle('is-unavailable', !variant.available);
      const checkbox = row.querySelector('[data-stack-check]');
      if (checkbox) {
        checkbox.disabled = !variant.available;
        if (!variant.available) checkbox.checked = false;
      }
      const price = row.querySelector('[data-stack-price]');
      if (price) {
        price.textContent = variant.available
          ? `${this.dataset.currentLabel} · ${formatMoney(variant.price, this.moneyFormat)}`
          : 'Sold out';
      }
      this.update();
    }

    ticked() {
      return this.rows.filter((row) => {
        const box = row.querySelector('[data-stack-check]');
        return box && box.checked && !box.disabled && row.dataset.available === 'true';
      });
    }

    update() {
      const ticked = this.ticked();
      const prices = ticked.map((row) => Number(row.dataset.price) || 0);
      const subtotal = prices.reduce((a, b) => a + b, 0);
      const eligible = ticked
        .filter((row) => row.dataset.eligible === 'true')
        .map((row) => Number(row.dataset.price) || 0)
        .sort((a, b) => a - b);
      const freeCount = Math.floor(eligible.length / 4);
      const saving = eligible.slice(0, freeCount).reduce((a, b) => a + b, 0);
      const total = subtotal - saving;

      // Summary line, e.g. "3 pieces + Titanium Sleepers 18G"
      const summary = this.querySelector('[data-stack-summary]');
      if (summary) {
        const currentTicked = ticked.some((row) => row.dataset.current === 'true');
        const others = ticked.length - (currentTicked ? 1 : 0);
        const pieces = (n) => `${n} ${n === 1 ? 'piece' : 'pieces'}`;
        const title = this.dataset.currentTitle || 'your piece';
        if (!ticked.length) summary.textContent = 'Tick the pieces you want';
        else if (currentTicked && others > 0) summary.textContent = `${pieces(others)} + ${title}`;
        else if (currentTicked) summary.textContent = `Just ${title}`;
        else summary.textContent = pieces(others);
      }

      const totalEl = this.querySelector('[data-stack-total]');
      const wasEl = this.querySelector('[data-stack-was]');
      const saveEl = this.querySelector('[data-stack-save]');
      const saveAmountEl = this.querySelector('[data-stack-save-amount]');
      if (totalEl) totalEl.textContent = formatMoney(total, this.moneyFormat);
      const qualifies = saving > 0;
      if (wasEl) {
        wasEl.hidden = !qualifies;
        wasEl.textContent = qualifies ? formatMoney(subtotal, this.moneyFormat) : '';
      }
      if (saveEl) saveEl.hidden = !qualifies;
      if (saveAmountEl) saveAmountEl.textContent = qualifies ? formatMoney(saving, this.moneyFormat) : '';

      if (this.button && !this.busy) {
        this.button.disabled = ticked.length === 0;
        if (this.buttonLabel) {
          this.buttonLabel.textContent = ticked.length
            ? (this.dataset.labelAdd || 'Add all [count]').replace('[count]', ticked.length)
            : this.dataset.labelEmpty || 'Select pieces';
        }
      }
    }

    async add() {
      const ticked = this.ticked();
      if (!ticked.length || this.busy) return;
      this.busy = true;
      this.button.classList.add('loading');
      this.button.setAttribute('aria-busy', 'true');
      if (this.error) this.error.hidden = true;

      try {
        await addItemsToCart(
          ticked.map((row) => ({ id: Number(row.dataset.variantId), quantity: 1 })),
          this.button,
          'pdp-stack'
        );
        if (this.buttonLabel) this.buttonLabel.textContent = this.dataset.labelAdded || 'Added to cart';
        setTimeout(() => {
          this.busy = false;
          this.update();
        }, 1600);
      } catch (error) {
        this.busy = false;
        if (this.error) {
          this.error.textContent = error.message;
          this.error.hidden = false;
        }
        this.update();
      } finally {
        this.button.classList.remove('loading');
        this.button.removeAttribute('aria-busy');
      }
    }
  }

  /* Quick add for the in-column "Pair it with" cards (complementary block on older templates). */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-pdp-quick-add]');
    if (!button || button.disabled) return;
    const scope = button.closest('[data-pdp-quick-add-scope]');
    const error = scope ? scope.querySelector('[data-pdp-error]') : null;
    if (error) error.hidden = true;
    button.disabled = true;
    button.classList.add('loading');
    try {
      await addItemsToCart([{ id: Number(button.dataset.pdpQuickAdd), quantity: 1 }], button, 'pdp-pair');
      button.textContent = button.dataset.labelAdded || 'Added';
      setTimeout(() => {
        button.textContent = button.dataset.label || 'Add';
        button.disabled = false;
      }, 1600);
    } catch (err) {
      button.disabled = false;
      if (error) {
        error.textContent = err.message;
        error.hidden = false;
      }
    } finally {
      button.classList.remove('loading');
    }
  });

  /* ==========================================================================
     <pdp-review-insights> — customer photo strip + Fit / Quality bars
     Source: REVIEWS.io public product reviews API (store key only).
     ========================================================================== */

  class PdpReviewInsights extends HTMLElement {
    connectedCallback() {
      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async load() {
      const skus = (this.dataset.skus || '')
        .split(';')
        .map((s) => s.trim())
        .filter(Boolean);
      if (!skus.length || !this.dataset.store) return;

      const params = new URLSearchParams({
        store: this.dataset.store,
        sku: skus.join(';'),
        per_page: '100',
        page: '1',
      });

      let reviews = [];
      try {
        const response = await fetch(`https://api.reviews.io/product/review?${params.toString()}`);
        if (!response.ok) return;
        const data = await response.json();
        reviews = (data && data.reviews && (data.reviews.data || data.reviews)) || [];
        if (!Array.isArray(reviews)) reviews = [];
      } catch (error) {
        return;
      }

      if (this.dataset.showPhotos === 'true') this.renderPhotos(reviews);
      if (this.dataset.showAttributes === 'true') this.renderAttributes(reviews);
    }

    photoUrl(item) {
      if (!item) return null;
      if (typeof item === 'string') return item;
      return item.full || item.large || item.url || item.src || item.image || item.thumbnail || null;
    }

    renderPhotos(reviews) {
      const strip = this.querySelector('[data-review-strip]');
      if (!strip) return;
      const photos = [];
      reviews.forEach((review) => {
        const list = review.images || review.photos || review.media || [];
        (Array.isArray(list) ? list : []).forEach((item) => {
          const src = this.photoUrl(item);
          if (src && !photos.some((p) => p.src === src)) {
            photos.push({ src, alt: `Customer photo from ${review.reviewer?.first_name || 'a customer'}` });
          }
        });
      });
      if (!photos.length) return;

      const visible = window.matchMedia('(max-width: 749px)').matches ? 4 : 8;
      const shown = photos.slice(0, visible);
      const extra = photos.length - shown.length;

      strip.innerHTML = '';
      shown.forEach((photo, index) => {
        const li = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pdp-reviews__photo';
        button.setAttribute('aria-label', `View customer photo ${index + 1} of ${photos.length}`);
        const img = document.createElement('img');
        img.src = photo.src;
        img.alt = '';
        img.loading = 'lazy';
        button.appendChild(img);
        if (extra > 0 && index === shown.length - 1) {
          const more = document.createElement('span');
          more.className = 'pdp-reviews__more';
          more.textContent = `+${extra} more`;
          button.appendChild(more);
          button.setAttribute('aria-label', `View all ${photos.length} customer photos`);
        }
        button.addEventListener('click', () => Lightbox.open(photos, index, button));
        li.appendChild(button);
        strip.appendChild(li);
      });
      strip.hidden = false;
    }

    renderAttributes(reviews) {
      const wrap = this.querySelector('[data-review-attrs]');
      if (!wrap) return;
      const defs = [1, 2]
        .map((n) => ({
          name: this.getAttribute(`data-attr-${n}`),
          min: this.getAttribute(`data-attr-${n}-min`),
          max: this.getAttribute(`data-attr-${n}-max`),
        }))
        .filter((d) => d.name);

      const bars = defs
        .map((def) => {
          const key = def.name.toLowerCase();
          const values = [];
          reviews.forEach((review) => {
            const sources = [review.ratings, review.sub_ratings, review.attributes, review.product_attributes];
            sources.forEach((source) => {
              if (!source) return;
              if (Array.isArray(source)) {
                source.forEach((entry) => {
                  const label = String(entry.name || entry.label || entry.attribute || '').toLowerCase();
                  const value = Number(entry.value ?? entry.rating ?? entry.score);
                  if (label === key && Number.isFinite(value)) values.push(value);
                });
              } else if (typeof source === 'object') {
                Object.keys(source).forEach((name) => {
                  const value = Number(source[name]);
                  if (name.toLowerCase() === key && Number.isFinite(value)) values.push(value);
                });
              }
            });
          });
          if (!values.length) return null;
          const average = values.reduce((a, b) => a + b, 0) / values.length;
          // Sub-ratings are 1–5; map onto the 0–100% track.
          const percent = Math.max(0, Math.min(100, ((average - 1) / 4) * 100));
          return { ...def, percent };
        })
        .filter(Boolean);

      if (!bars.length) return;
      wrap.innerHTML = bars
        .map(
          (bar) => `
          <div class="pdp-reviews__attr">
            <p class="pdp-reviews__attr-label">${escapeHtml(bar.name)}</p>
            <div class="pdp-reviews__track" role="img" aria-label="${escapeHtml(bar.name)}: ${Math.round(
            bar.percent
          )}% towards ${escapeHtml(bar.max || '')}">
              <span class="pdp-reviews__fill" style="width:${bar.percent.toFixed(1)}%"></span>
            </div>
            <div class="pdp-reviews__ends" aria-hidden="true"><span>${escapeHtml(bar.min || '')}</span><span>${escapeHtml(
            bar.max || ''
          )}</span></div>
          </div>`
        )
        .join('');
      wrap.hidden = false;
    }
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  /* ---------- Register ---------- */

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };
  /* <pdp-info> — info callout: shows on hover (desktop), tap/click toggles it, Escape or a click elsewhere closes */
  class PdpInfo extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.onOutside = (event) => {
        if (!this.contains(event.target)) this.toggle(false);
      };
      this.button.addEventListener('click', () => this.toggle(this.button.getAttribute('aria-expanded') !== 'true'));
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || this.button.getAttribute('aria-expanded') !== 'true') return;
        this.toggle(false);
        this.button.focus();
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onOutside);
    }

    toggle(open) {
      this.button.setAttribute('aria-expanded', String(open));
      this.toggleAttribute('open', open);
      if (open) document.addEventListener('click', this.onOutside);
      else document.removeEventListener('click', this.onOutside);
    }
  }

  define('pdp-product', PdpProduct);
  define('pdp-info', PdpInfo);
  define('pdp-gallery', PdpGallery);
  define('pdp-countdown', PdpCountdown);
  define('pdp-stack', PdpStack);
  define('pdp-review-insights', PdpReviewInsights);
})();
